import { type FC, useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import type { ApolloError } from "@apollo/client";
import { AutoSizer, VirtualTable, usePrevious } from "@snapchat/snap-design-system";
import { SortOrder } from "@snapchat/snap-design-system/lib/components/VirtualTable/customPlugins/useSdsSortBy";

import { ActionDisabledTooltip } from "../../../common/components/ActionDisabledTooltip";
import { clickableRowCss } from "../../../common/components/table/styles";
import { useTableFilterAndSort } from "../../../common/hooks/useTableFilterAndSort";
import { GroupsGridItemModel } from "../../grid/model";
import { NoResultsCard } from "../GroupsGrid.styles";
import { NoResults } from "../NoResults";
import { AddItem } from "../AddItem";
import { AccessContainer } from "../../../common/containers/AccessContainer";
import { SearchableCardWithUpsellBanner } from "../../../common/components/SearchableCardWithUpsellBanner";
import { usePinnedGroups } from "../../hooks/usePinnedGroups";

import { getColumns } from "./GroupsTableColumns";

interface GroupsTableContainerProps {
    readonly: boolean;
    onNewLensGroup: () => void;
}

export const GroupsTableContainer: FC<GroupsTableContainerProps> = ({ readonly, onNewLensGroup }) => {
    const { groups, loading, error, hasMore, fetchMore, pinGroup, groupBeingPinned } = usePinnedGroups();
    return (
        <GroupsTable
            fetchMore={fetchMore}
            isLoading={loading}
            error={error}
            groups={groups}
            onNewLensGroup={onNewLensGroup}
            hasMore={hasMore}
            pinGroup={pinGroup}
            groupBeingPinned={groupBeingPinned}
            readonly={readonly}
        />
    );
};

type ExtendedGroupsGridItemModel = GroupsGridItemModel & Record<string, unknown>;

interface GroupsTableProps extends GroupsTableContainerProps {
    groups: readonly ExtendedGroupsGridItemModel[];
    isLoading: boolean;
    hasMore: boolean;
    error?: ApolloError;
    fetchMore: () => void;
    pinGroup: (groupId: string, toPin: boolean) => void;
    groupBeingPinned?: string;
}

const GroupsTable: FC<GroupsTableProps> = ({
    groups,
    isLoading,
    hasMore,
    error,
    fetchMore,
    pinGroup,
    groupBeingPinned,
    onNewLensGroup,
}: GroupsTableProps) => {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");

    const getSearchKey = useCallback((group: ExtendedGroupsGridItemModel) => `${group.name} ${group.id}`, []);

    const { data, onColumnSort } = useTableFilterAndSort({
        tableData: groups,
        initialColumn: "lastUpdated",
        initialSortOrder: SortOrder.Desc,
        searchText,
        getSearchKey,
        initialRemoveFromSortKey: "isPinned",
    });

    // NOTE: There is a bug in VirtualTable when
    // 1) next data array has less items than what it currently holds, and
    // 2) the first page is rendered and visible,
    // then VirtualTable stops asking for more items.
    // Due to implementation of VirtualTable it was hard to locate the issue.
    // Looks like it goes whole the way down to thrid-party InfiniteLoader or VariableSizeGrid.
    // The workaround is just to re-mount the grid when new data array has less items
    // than what it currently holds. That happens, for example, when groups cache is evicted.
    const prev = usePrevious(groups);
    const key = groups.length < (prev?.length ?? 0) ? "re-mount" : "keep-mounted";

    const onGroupClick = useCallback(
        (index: number) => {
            const group = data[index];
            navigate(group?.id ?? "", { relative: "path" });
        },
        [data, navigate],
    );

    const onToggleGroupPin = useCallback(
        (group: GroupsGridItemModel, toPin: boolean) => {
            pinGroup(group?.id!, toPin);
        },
        [pinGroup],
    );

    const getTableColumns = useCallback(
        (width: number) => getColumns(onToggleGroupPin, groupBeingPinned!, width),
        [groupBeingPinned, onToggleGroupPin],
    );

    const onSearch = useCallback(
        (text: string) => {
            setSearchText(text);
        },
        [setSearchText],
    );

    useEffect(() => {
        if (!isLoading && hasMore) {
            fetchMore();
        }
    }, [fetchMore, hasMore, isLoading]);

    return (
        <>
            <SearchableCardWithUpsellBanner
                searchPlaceholder="Search by Lens Group ID or Name"
                padBody={false}
                searchValue={searchText}
                onSearch={onSearch}
                isLoading={hasMore}
                loadingTooltip="Groups are still loading."
                error={error}
            >
                <AutoSizer>
                    {({ width, height }: { width: number; height: number }) => (
                        <VirtualTable
                            key={key}
                            isLoading={isLoading && data.length === 0}
                            data={data as ExtendedGroupsGridItemModel[]}
                            columns={getTableColumns(width)}
                            hasMore={hasMore}
                            width={width}
                            height={height}
                            rowHeight={64}
                            rowClassName={clickableRowCss}
                            emptyMessage={
                                <NoResultsCard titleBorder={false} padBody={false}>
                                    <NoResults
                                        description="No group found."
                                        addItem={
                                            <AccessContainer>
                                                {({ permissions }) => (
                                                    <ActionDisabledTooltip
                                                        placement="top"
                                                        hasPermission={permissions.updateGroups!}
                                                    >
                                                        <AddItem
                                                            onAddItem={onNewLensGroup}
                                                            onAddItemButtonText="New Lens Group"
                                                            isDisabled={!permissions.updateGroups}
                                                        />
                                                    </ActionDisabledTooltip>
                                                )}
                                            </AccessContainer>
                                        }
                                    />
                                </NoResultsCard>
                            }
                            loadMore={async () => {
                                fetchMore();
                            }}
                            onRowClick={(_, rowIndex: number) => {
                                onGroupClick(rowIndex);
                            }}
                            onColumnSort={onColumnSort}
                        />
                    )}
                </AutoSizer>
            </SearchableCardWithUpsellBanner>
        </>
    );
};
